import { useState, useEffect } from "react";
import { G, ADMIN_API, ADMIN_MODULES, IcoKB, IcoLeads, IcoUsers, IcoLogs, IcoChatBot, IcoAudit, IcoPortfolio } from "../../design/tokens";
import { getAccount, getAccessToken, login as msalLogin, logout as msalLogout } from "../../lib/auth";
import KBModule from "./KBModule";
import LeadsModule from "./LeadsModule";
import UsersModule from "./UsersModule";
import LogsModule from "./LogsModule";
import ChatBotModule from "./ChatBotModule";
import AuditModule from "./AuditModule";
import PortfolioModule from "./PortfolioModule";

export default function AdminPanel() {
  const [token, setToken]               = useState("");
  const [account, setAccount]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [activeModule, setActiveModule] = useState("leads");
  const [sidebarOpen, setSidebarOpen]   = useState(true);
  const [status, setStatus]             = useState("");
  const [darkMode, setDarkMode]         = useState(localStorage.getItem("nesw_admin_theme") !== "light");

  useEffect(() => {
    (async () => {
      try {
        const acc = await getAccount();
        if (acc) {
          setAccount(acc);
          const t = await getAccessToken();
          if (t) {
            setToken(t);
            fetch(`${ADMIN_API}/login`, {
              method: "POST",
              headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
            }).catch(() => {});
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleTheme = () => setDarkMode(d => {
    const next = !d;
    localStorage.setItem("nesw_admin_theme", next ? "dark" : "light");
    return next;
  });

  const T = darkMode ? {
    bg: G.bg, sidebar: G.sidebar, glass: G.glass, glassBrd: G.glassBrd,
    text: "#fff", textMuted: "rgba(255,255,255,0.4)", textFaint: "rgba(255,255,255,0.25)",
    border: "rgba(255,255,255,0.05)", inputBg: "rgba(6,14,30,0.6)", inputBorder: "rgba(255,255,255,0.1)",
    topbar: "rgba(6,14,30,0.4)", thBg: "rgba(4,10,24,0.6)", rowHover: "rgba(37,99,235,0.06)",
    rowBorder: "rgba(255,255,255,0.03)", cardBg: "rgba(11,29,58,0.65)",
  } : {
    bg: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
    sidebar: "#1E3A6E",
    glass: "#fff", glassBrd: "#BFDBFE",
    text: "#0B1D3A", textMuted: "#475569", textFaint: "#94A3B8",
    border: "#E2E8F0", inputBg: "#F8FAFC", inputBorder: "#BFDBFE",
    topbar: "rgba(255,255,255,0.9)", thBg: "#EFF6FF", rowHover: "#DBEAFE",
    rowBorder: "#E2E8F0", cardBg: "#fff",
  };

  const loggedUser = account?.username || account?.name || "";
  const authHeader = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  const canAccess  = () => true;
  const canWrite   = () => true;

  const MODULE_ICONS = { kb: <IcoKB />, leads: <IcoLeads />, users: <IcoUsers />, logs: <IcoLogs />, chatbot: <IcoChatBot />, audit: <IcoAudit />, portfolio: <IcoPortfolio /> };

  // ── Loading
  if (loading) return (
    <div style={{ minHeight: "100vh", background: G.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "rgba(255,255,255,0.35)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, letterSpacing: 0.5 }}>Authenticating…</div>
    </div>
  );

  // ── Login screen
  if (!account) return (
    <div style={{ minHeight: "100vh", background: G.bg, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "-20%", left: "-10%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-20%", right: "-10%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ background: "rgba(11,29,58,0.6)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 22, padding: "52px 44px", width: 420, boxShadow: "0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(37,99,235,0.08)", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 40 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: G.btnGrad, boxShadow: G.btnShadow, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1 }}>NESW Admin</div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.35)", letterSpacing: 1, marginTop: 2 }}>Management Portal</div>
          </div>
        </div>

        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 20, lineHeight: 1.6 }}>
          Sign in with your NESW Microsoft 365 account to access the admin portal.
        </div>

        <button onClick={msalLogin} style={{ width: "100%", padding: "13px 16px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.06)", color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, transition: "all 0.15s", letterSpacing: 0.3 }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
        >
          <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1h10v10H1z" fill="#F25022"/>
            <path d="M12 1h10v10H12z" fill="#7FBA00"/>
            <path d="M1 12h10v10H1z" fill="#00A4EF"/>
            <path d="M12 12h10v10H12z" fill="#FFB900"/>
          </svg>
          Sign in with Microsoft 365
        </button>
      </div>
    </div>
  );

  // ── Dashboard
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex" }}>
      {/* Sidebar */}
      <div style={{ width: sidebarOpen ? 232 : 64, background: T.sidebar, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", flexShrink: 0, transition: "width 0.2s ease", overflow: "hidden" }}>
        {/* Brand */}
        <div style={{ padding: "20px 14px 16px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "center", gap: 10, justifyContent: sidebarOpen ? "space-between" : "center" }}>
          {sidebarOpen && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: G.btnGrad, boxShadow: G.btnShadow, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1 }}>NESW</div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginTop: 1 }}>Admin Portal</div>
              </div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(o => !o)} title={sidebarOpen ? "Collapse" : "Expand"} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav style={{ padding: "16px 8px", flex: 1 }}>
          {sidebarOpen && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textTransform: "uppercase", padding: "0 8px", marginBottom: 8 }}>Modules</div>}
          {ADMIN_MODULES.filter(m => canAccess(m.id)).map(m => {
            const active = activeModule === m.id;
            return (
              <button key={m.id} onClick={() => setActiveModule(m.id)} title={!sidebarOpen ? m.label : undefined} style={{ width: "100%", textAlign: "left", padding: sidebarOpen ? "10px 14px" : "10px 0", borderRadius: 10, marginBottom: 3, border: "none", background: active ? "rgba(37,99,235,0.15)" : "transparent", color: active ? "#fff" : "rgba(255,255,255,0.4)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: sidebarOpen ? 11 : 0, justifyContent: sidebarOpen ? "flex-start" : "center", transition: "all 0.15s", position: "relative", boxShadow: active ? "inset 0 0 0 1px rgba(37,99,235,0.35)" : "none" }}>
                {active && sidebarOpen && <div style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: 3, height: 18, borderRadius: "0 3px 3px 0", background: G.btnGrad, boxShadow: "0 0 8px rgba(37,99,235,0.6)" }} />}
                <span style={{ color: active ? "#93C5FD" : "rgba(255,255,255,0.3)", transition: "color 0.15s" }}>{MODULE_ICONS[m.id]}</span>
                {sidebarOpen && m.label}
              </button>
            );
          })}
        </nav>

        {/* User / Logout */}
        <div style={{ padding: "14px 8px 20px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
          {sidebarOpen && status && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: "#34D399", padding: "6px 10px", background: "rgba(52,211,153,0.08)", borderRadius: 7, marginBottom: 10, border: "1px solid rgba(52,211,153,0.15)" }}>{status}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: sidebarOpen ? 10 : 0, justifyContent: sidebarOpen ? "flex-start" : "center", padding: sidebarOpen ? "10px 10px" : "10px 0", borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: G.btnGrad, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "#fff", fontWeight: 700, flexShrink: 0 }}>
              {loggedUser[0]?.toUpperCase() || "A"}
            </div>
            {sidebarOpen && (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: "#fff", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{loggedUser}</div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)" }}>Administrator</div>
                </div>
                <button onClick={msalLogout} title="Logout" style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* Top bar */}
        <div style={{ padding: "14px 32px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: T.topbar, backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 10, flexShrink: 0 }}>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: T.textFaint, letterSpacing: 1.5, textTransform: "uppercase" }}>
            {ADMIN_MODULES.find(m => m.id === activeModule)?.label}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#34D399", boxShadow: "0 0 6px #34D399" }} />
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: T.textFaint }}>Live</span>
            </div>
            <button onClick={toggleTheme} title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"} style={{ width: 34, height: 34, borderRadius: 9, border: `1px solid ${T.border}`, background: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", color: T.textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {darkMode
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              }
            </button>
          </div>
        </div>

        {/* Module */}
        <div style={{ flex: 1 }}>
          {activeModule === "leads"     && <LeadsModule     authHeader={authHeader} canWrite={canWrite("leads")} loggedUser={loggedUser} />}
          {activeModule === "portfolio" && <PortfolioModule authHeader={authHeader} canWrite={canWrite("portfolio")} />}
          {activeModule === "chatbot"   && <ChatBotModule   authHeader={authHeader} canWrite={canWrite("chatbot")} />}
          {activeModule === "users"     && <UsersModule     authHeader={authHeader} canWrite={canWrite("users")} loggedUser={loggedUser} />}
          {activeModule === "logs"      && <LogsModule      authHeader={authHeader} />}
          {activeModule === "kb"        && <KBModule        authHeader={authHeader} onStatus={setStatus} canWrite={canWrite("kb")} />}
          {activeModule === "audit"     && <AuditModule     authHeader={authHeader} />}
        </div>
      </div>
    </div>
  );
}
